import { useState, useEffect, useRef } from 'react';

const testimonials = [
  {
    name: 'Valentina Moreno',
    role: 'Coach Somática · Madrid, España',
    quote:
      'El módulo de Bio-Santuario transformó mi espacio vital en un verdadero anclaje nodal. Experimenté una mejora del 40% en la calidad de mi sueño y una presencia que nunca había sentido antes. La Re-ingeniería Sistémica es exactamente lo que estaba buscando.',
    highlight: '"40% mejora en bienestar"',
    img: 'https://public.readdy.ai/ai/img_res/1b92e58406d837f9bd42101b0130a7c4.jpg',
  },
  {
    name: 'Rodrigo Castellanos',
    role: 'Terapeuta Sistémico · Buenos Aires, Argentina',
    quote:
      'Las Constelaciones como Blueprint Sistémico me dieron un lenguaje técnico para lo que sentía intuitivamente. Comprender la Tensión Nodal en mi sistema familiar fue el punto de quiebre. Hoy opero desde una soberanía que creí imposible.',
    highlight: '"Soberanía sistémica real"',
    img: 'https://readdy.ai/api/search-image?query=portrait%20of%20thoughtful%20mature%20man%20with%20short%20beard%20warm%20terracotta%20lighting%20minimal%20backdrop%20with%20subtle%20geometric%20patterns%20wellness%20practitioner%20look%20confident%20expression%20natural%20clean%20professional%20Mediterranean%20features&width=500&height=600&seq=testimonial2-nube-001&orientation=portrait',
  },
  {
    name: 'Amara Silva',
    role: 'Facilitadora de Bienestar · Lisboa, Portugal',
    quote:
      'Llegué al Horizonte Arraigado buscando grounding. Encontré una metodología completa. El Astrolabio Interior me enseñó a navegar mis estados emocionales como tecnología, no como obstáculos. Mi vida entera es ahora un campo coherente.',
    highlight: '"Metodología completa y transformadora"',
    img: 'https://readdy.ai/api/search-image?query=portrait%20of%20young%20woman%20with%20clear%20eyes%20and%20natural%20makeup%20sage%20green%20nature%20background%20warm%20golden%20hour%20light%20spiritual%20seeker%20aesthetic%20candid%20genuine%20expression%20minimal%20professional%20authentic%20Brazilian%20features&width=500&height=600&seq=testimonial3-nube-001&orientation=portrait',
  },
];

export default function ResonanciaSection() {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [tickerPos, setTickerPos] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const tickerRef = useRef<number>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const animate = () => {
      setTickerPos((prev) => (prev <= -50 ? 0 : prev - 0.03));
      tickerRef.current = requestAnimationFrame(animate);
    };
    tickerRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(tickerRef.current);
  }, []);

  const tickerItems = [
    '⊹ 1,247 Nodos Activos',
    '— Alineación Nodal en Curso',
    '⊹ 38 Nuevos Nodos esta semana',
    '— Tensión Sistémica Liberada: 94%',
    '⊹ Ayni en Reciprocidad Activa',
    '— Red Geodésica Expandiéndose',
  ];

  const t = testimonials[current];

  return (
    <section id="resonancia" ref={sectionRef} className="bg-[#8B9D83] py-32 overflow-hidden">
      {/* Live ticker */}
      <div className="bg-[#1A1C20] py-3 mb-16 overflow-hidden">
        <div
          className="flex gap-16 whitespace-nowrap text-xs uppercase tracking-[2px] text-[#F8F6F3]/60"
          style={{ transform: `translateX(${tickerPos}%)`, transition: 'none' }}
        >
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="flex-shrink-0">{item}</span>
          ))}
        </div>
      </div>

      <div
        className={`max-w-screen-xl mx-auto px-6 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#1A1C20]/60 text-xs uppercase tracking-[4px] mb-4">( Resonancia de la Red )</p>
          <h2
            className="text-[#1A1C20] font-bold leading-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
          >
            Voces del campo sistémico
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: portrait */}
          <div className="relative">
            <div
              className="rounded-2xl overflow-hidden w-full"
              style={{ height: '520px' }}
            >
              <img
                src={t.img}
                alt={t.name}
                className="w-full h-full object-cover object-top transition-all duration-500"
              />
            </div>
            {/* Highlight badge */}
            <div className="absolute bottom-6 left-6 bg-[#1A1C20] text-[#C97B63] text-sm font-semibold px-5 py-3 rounded-xl">
              {t.highlight}
            </div>
          </div>

          {/* Right: quote */}
          <div>
            <p className="text-[#1A1C20]/50 text-xs uppercase tracking-[3px] mb-6">( Testimonio de la Red )</p>

            <div className="mb-2">
              <span className="text-[#C97B63] font-serif leading-none" style={{ fontSize: '4rem', lineHeight: 1 }}>&ldquo;</span>
            </div>

            <p className="text-[#1A1C20]/85 text-lg leading-relaxed max-w-md mb-8">
              {t.quote}
            </p>

            <div className="mb-10">
              <span className="text-[#1A1C20] font-bold text-base">— {t.name}</span>
              <p className="text-[#1A1C20]/60 text-sm mt-1">{t.role}</p>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length)}
                className="w-12 h-12 flex items-center justify-center border-2 border-[#1A1C20]/30 rounded-full hover:border-[#1A1C20] transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-[#1A1C20]" />
              </button>
              <button
                onClick={() => setCurrent((c) => (c + 1) % testimonials.length)}
                className="w-12 h-12 flex items-center justify-center bg-[#C97B63] rounded-full hover:bg-[#b86d55] transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-right-line text-[#F8F6F3]" />
              </button>
              <div className="flex gap-2 ml-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      i === current ? 'w-6 h-2 bg-[#C97B63]' : 'w-2 h-2 bg-[#1A1C20]/30'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
